import React from 'react';
import {classes, style} from './doppe-action-type-product-widget.st.css';
import {
    DoppeActionTypeProduct,
    doppeActionTypeProductResolveSettingsFromBoundEntity
} from '../doppe-action-type-product';
import {
    doppeActionPriceDataGetActualPrice,
    doppeActionPriceDataGetDiscountPrice,
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsHidden
} from '../../../client-server-common';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    CouponSourceType,
    DoppeActionTypeCouponView
} from '../../../components/doppe-action-type-coupon-view/doppe-action-type-coupon-view';
import {
    bymoPageContextPropsResolveBusinessEntity,
    IDoppeViewerActionContext,
    useBymoPageContextProps
} from '../../../doppe-sdk';
import {
    asArray,
    evaluateFunction,
    HtmlObjectResizeMode,
    stringIsNotNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppePageMediaView,
    DoppePageMediaViewMediaBackground
} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {
    DoppeActionTypePriceDataView
} from '../../../components/doppe-action-type-price-data-view/doppe-action-type-price-data-view';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';

export const DoppeActionTypeProductWidget = React.memo(function DoppeActionTypeProductWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeProduct.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;


    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeProductBindableWidget
                actionContext={actionContext}
                actionId={actionContext.action.id}
                reportGoalAchieved={(goalType) => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    });
                }}
                actionSettings={actionContext.action.settings}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeProductBindableWidget = React.memo(function DoppeActionTypeProductBindableWidget(
    props: {
        actionSettings: DoppeActionTypeProduct.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionContext,
        actionSettings,
        reportGoalAchieved,
        actionId,
        listItemId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();
    const {
        productProvider,
        productProviderId
    } = actionSettings;

    if (productProvider !== null && productProviderId !== null) {
        const boundProduct = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalProductProviderResolveBusinessEntityReference(productProvider, productProviderId))

        if (!boundProduct || doppeBusinessEntityDtoIsNotFound(boundProduct)) {
            return (
                <DoppeActionTypeProductWidgetContentUnavailable
                    reason={
                        !boundProduct ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            return (
                <DoppeActionTypeProductWidgetInner
                    actionContext={actionContext}
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={{
                        ...actionSettings,
                        ...doppeActionTypeProductResolveSettingsFromBoundEntity(actionSettings, boundProduct),
                    }}
                    listItemId={listItemId}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeProductWidgetInner
                actionContext={actionContext}
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
                listItemId={listItemId}
            />
        )
    }
});


export const DoppeActionTypeProductWidgetInner = React.memo(function DoppeActionTypeProductWidgetInner(
    props: {
        actionSettings: DoppeActionTypeProduct.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
        actionContext,
        listItemId
    } = props;

    const {
        productPageLink,
        priceData,
        productImage,
        couponCode,
    } = actionSettings;

    const productTitle = doppeHideableValueGetVisibleValue(actionSettings.productTitle, '');
    const description = doppeHideableValueGetVisibleValue(actionSettings.description, '');

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
            actionContext={actionContext}
            reportGoalAchieved={reportGoalAchieved}
            mainMedia={asArray(productImage)[0] ? <DoppePageMediaView
                    className={style(classes.productMedia, {})}
                    mediaBackground={DoppePageMediaViewMediaBackground.Auto}
                    mediaResource={asArray(productImage)[0]}
                    layoutSpec={{autoHeight: true, maxHeight: 360}}
                    widthSpec={{contentRelativeWidth: 1}}
                    mediaResizeMode={HtmlObjectResizeMode.Contain}
                />
                : null}
            sections={actionSettings.sections}
            buttons={actionSettings.ctaButtons}
            buttonsSettings={{
                sourceURL: stringIsNotNullableOrWhiteSpace(productPageLink) ? productPageLink : 'about:blank',
                listItemId: listItemId,
            }}
        >
            {productTitle !== '' || description !== '' ? (
                <div
                    className={style(classes.productNameAndDescription, {})}
                >
                    <DoppeActionTypeWidgetInnerTitle
                        className={style(classes.productName, {})}
                    >
                        {productTitle}
                    </DoppeActionTypeWidgetInnerTitle>
                    <DoppeActionTypeWidgetInnerDescription
                        className={style(classes.productDescription, {})}
                    >
                        {description}
                    </DoppeActionTypeWidgetInnerDescription>
                </div>
            ) : null}
            {!doppeHideableValueIsHidden(priceData) ? evaluateFunction(() => {
                const actualPrice = doppeActionPriceDataGetActualPrice(priceData);
                const discountPrice = doppeActionPriceDataGetDiscountPrice(priceData);

                if (!actualPrice && !discountPrice) {
                    return null;
                }

                return (
                    <DoppeActionTypePriceDataView
                        className={style(classes.priceContainer, {})}
                        priceData={priceData}
                    />
                )
            }) : null}
            {
                !doppeHideableValueIsHidden(couponCode) ?
                    <DoppeActionTypeCouponView
                        className={style(classes.couponView, {})}
                        coupon={couponCode}
                        couponSourceType={CouponSourceType.Action}
                        actionId={actionId}
                        onCouponCopied={() => {
                            reportGoalAchieved(DoppeActionGoalType.CouponCopied);
                        }}
                    />
                    :
                    null
            }
        </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
    );
});

export const DoppeActionTypeProductWidgetContentUnavailable = React.memo(function DoppeActionTypeProductWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitleProduct']}
        />
    )
});